"use client";

import { useState } from "react";

import { StatusPill } from "@/components/connections/status-pill";
import { api } from "@/lib/api/client";
import type { Connection, CostReport } from "@/lib/api/types";
import { formatAmount, percent } from "@/lib/cost-report";
import { formatCount, formatDateTime, formatEuro } from "@/lib/format";

type Props = {
  report: CostReport;
  projectId: number;
  /** Nach einer Prüfung: die aktualisierte Verbindung (Guthaben steht in der Statusmeldung). */
  onChecked: (connection: Connection) => void;
};

/** Verbrauch je Quelle in ihrer Einheit, mit dem Guthaben aus der letzten Verbindungsprüfung zum Abgleich. */
export function SourcesTable({ report, projectId, onChecked }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const check = async (source: string) => {
    setBusy(source);
    setErrors((current) => ({ ...current, [source]: "" }));
    try {
      onChecked(await api<Connection>(`/api/connections/${source}/test?project_id=${projectId}`, { method: "POST" }));
    } catch (caught) {
      setErrors((current) => ({ ...current, [source]: caught instanceof Error ? caught.message : "Unbekannter Fehler" }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-labelledby="sources-title" className="rounded-lg border border-line bg-white">
      <div className="flex flex-col gap-0.5 border-b border-[#e6e9e7] px-5 py-4">
        <h2 id="sources-title" className="text-base font-semibold">
          Nach Quelle
        </h2>
        <span className="text-xs text-muted">
          Verbrauch in der Einheit der Quelle. „Prüfen“ liest das aktuelle Guthaben der Quelle (kostenlos), damit sich das
          Kosten-Log damit abgleichen lässt.
        </span>
      </div>
      {report.sources.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted">In diesem Monat gab es keine Abrufe.</p>
      ) : (
        <div className="overflow-x-auto">
          <table aria-label="Kosten nach Quelle" className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="px-5 py-2.5 font-medium">Quelle</th>
                <th className="px-3 py-2.5 text-right font-medium">Verbrauch</th>
                <th className="px-3 py-2.5 text-right font-medium">Abrufe</th>
                <th className="px-3 py-2.5 text-right font-medium">davon Cache</th>
                <th className="px-3 py-2.5 text-right font-medium">Euro-Kosten</th>
                <th className="px-5 py-2.5 font-medium">Guthaben und Status</th>
              </tr>
            </thead>
            <tbody>
              {report.sources.map((row) => (
                <tr key={row.source} className="border-t border-neutral-bg align-top">
                  <th scope="row" className="px-5 py-3 text-left font-medium">
                    {row.label}
                  </th>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-[13px]">
                    {formatAmount(row.amount, row.unit)}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-[13px]">{formatCount(row.requests)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-[13px]">
                    {formatCount(row.cache_hits)}
                    <span className="text-muted">
                      {" "}
                      ({row.requests ? percent(row.cache_hits / row.requests) : 0} %)
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-[13px]">
                    {row.cost_eur > 0 ? formatEuro(row.cost_eur) : "–"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                      {row.status && <StatusPill status={row.status} />}
                      <span className="min-w-0 flex-1 text-[13px] text-[#3a424a]">
                        {row.status_message ?? "Nicht verbunden"}
                        {row.tested_at && (
                          <span className="block text-xs text-muted">Geprüft am {formatDateTime(row.tested_at)}</span>
                        )}
                        {errors[row.source] && (
                          <span role="alert" className="block text-xs text-danger">
                            {errors[row.source]}
                          </span>
                        )}
                      </span>
                      {row.status !== null && row.status !== "not_connected" && (
                        <button
                          type="button"
                          onClick={() => void check(row.source)}
                          disabled={busy === row.source}
                          aria-label={`Guthaben prüfen: ${row.label}`}
                          className="h-11 rounded-md border border-field bg-white px-3 text-[13px] disabled:opacity-60"
                        >
                          {busy === row.source ? "Prüfe …" : "Prüfen"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
